import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { supportEmail, usePublicSettings } from '../shared/settings/workspaceSettings';
import {
  ArrowLeft, ChevronRight, CircleHelp, Compass, FileText, Keyboard, Mail, Pencil, Plus, Search, ShieldCheck, Sparkles, X,
} from 'lucide-react';
import { Link } from 'react-router';
import { useI18n } from '../i18n/I18nProvider';
import HelpBody from './HelpBody';
import HelpEditor from './HelpEditor';
import { useHelp, unreadNews, tourStatus, type HelpView } from './HelpContext';
import { feedbackMailto } from './feedback';
import { audienceLabelKey, primaryHelpRole } from './helpRoles';
import { helpPlainText } from './markdown';
import { bestRouteScore, matchRoute } from './routeMatch';
import { searchHelp } from './search';
import { SHORTCUT_GROUPS, isMac } from './shortcuts';
import { getTour } from './tours';
import type { HelpEntry, TourDef } from './types';
import './help.css';

const PHONE_QUERY = '(max-width: 599px)';

function isPhone(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && !!window.matchMedia(PHONE_QUERY)?.matches;
}

/** Topics for this address, the most specific route first. */
export function topicsForPage(topics: HelpEntry[], pathname: string): HelpEntry[] {
  return topics
    .map((topic) => ({ topic, score: bestRouteScore(topic.routes, pathname) }))
    .filter((x) => x.score >= 0)
    .sort((a, b) => b.score - a.score || a.topic.order - b.topic.order || a.topic.title.localeCompare(b.topic.title))
    .map((x) => x.topic);
}

/** Tours that run on this page or belong to one of its topics, among those this person may take. */
export function toursForPage(tours: TourDef[], pageTopics: HelpEntry[], pathname: string): TourDef[] {
  const ids = new Set(pageTopics.map((t) => t.tour).filter(Boolean) as string[]);
  return tours.filter((tour) => matchRoute(tour.route, pathname) || ids.has(tour.id));
}

/**
 * The help panel: a side panel on a computer (the page stays usable beside it)
 * and a full-screen sheet on a phone. Escape closes it and focus goes back to
 * the button that opened it.
 */
export default function HelpPanel() {
  const { t } = useI18n();
  const help = useHelp()!;
  const { open, view } = help.state;
  const { closePanel, setView } = help.actions;
  const titleId = useId();
  const panelRef = useRef<HTMLElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState('');

  // On opening (and on every change of view) focus goes into the panel: the search box on a
  // computer's home view, otherwise the heading -- a phone's keyboard should not pop up unasked.
  useEffect(() => {
    if (!open) return;
    const frame = window.requestAnimationFrame(() => {
      if (view.name === 'home' && !isPhone()) searchRef.current?.focus();
      else titleRef.current?.focus();
      panelRef.current?.querySelector('.khelp-scroll')?.scrollTo?.(0, 0);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [open, view]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      // A dialog opened over the page (a modal, the image viewer) closes first.
      const modal = document.querySelector('[aria-modal="true"]');
      if (modal && !panelRef.current?.contains(modal)) return;
      if (modal) return;
      closePanel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, closePanel]);

  const back = useCallback(() => setView({ name: 'home' }), [setView]);
  const wide = view.name === 'edit';

  let title = t('help.title');
  if (view.name === 'news') title = t('help.news.title');
  else if (view.name === 'shortcuts') title = t('help.shortcuts.title');
  else if (view.name === 'edit') title = view.id ? t('help.editor.editTitle') : (view.kind === 'news' ? t('help.editor.newNews') : t('help.editor.newTopic'));
  else if (view.name === 'topic') title = t('help.topicTitle');

  return (
    <aside
      ref={panelRef}
      id="khelp-panel"
      className={`khelp-panel${wide ? ' khelp-panel--wide' : ''}`}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      hidden={!open}
      data-help-panel=""
      // The quiet mode bubble steps aside while help is open.
      {...(open ? { 'data-hides-quiet-bubble': '' } : {})}
    >
      <header className="khelp-head">
        {view.name !== 'home' ? (
          <button type="button" className="khelp-icon-btn" onClick={view.name === 'edit' && view.id ? () => setView({ name: view.kind === 'news' ? 'news' : 'topic', id: view.id! } as HelpView) : back} aria-label={t('help.back')}>
            <ArrowLeft size={18} aria-hidden="true" />
          </button>
        ) : (
          <span className="khelp-head__mark" aria-hidden="true"><CircleHelp size={18} /></span>
        )}
        <h2 id={titleId} ref={titleRef} tabIndex={-1} className="khelp-head__title">{title}</h2>
        <button type="button" className="khelp-icon-btn" onClick={closePanel} aria-label={t('help.close')}>
          <X size={18} aria-hidden="true" />
        </button>
      </header>

      <div className="khelp-scroll">
        {open && view.name === 'home' && <HomeView query={query} setQuery={setQuery} searchRef={searchRef} />}
        {open && view.name === 'topic' && <TopicView id={view.id} />}
        {open && view.name === 'news' && <NewsView />}
        {open && view.name === 'shortcuts' && <ShortcutsView />}
        {open && view.name === 'edit' && <HelpEditor id={view.id} kind={view.kind} />}
      </div>
    </aside>
  );
}

function AudienceChip({ roles }: { roles: string[] }) {
  const { t } = useI18n();
  const key = audienceLabelKey(roles);
  return key ? <span className="khelp-chip">{t(key)}</span> : null;
}

function TopicRow({ topic, onOpen, snippet }: { topic: HelpEntry; onOpen: () => void; snippet?: string }) {
  return (
    <li>
      <button type="button" className="khelp-row" onClick={onOpen}>
        <span className="khelp-row__text">
          <span className="khelp-row__title">{topic.title}<AudienceChip roles={topic.roles} /></span>
          {(snippet || topic.summary) && <span className="khelp-row__summary">{snippet || topic.summary}</span>}
        </span>
        <ChevronRight size={16} aria-hidden="true" className="khelp-row__go" />
      </button>
    </li>
  );
}

function TourCard({ tour }: { tour: TourDef }) {
  const { t } = useI18n();
  const help = useHelp()!;
  const status = tourStatus(tour, help.state.helpState);
  return (
    <div className="khelp-tour">
      <span className="khelp-tour__icon" aria-hidden="true"><Compass size={18} /></span>
      <span className="khelp-tour__text">
        <span className="khelp-tour__title">{t(`help.tours.${tour.id}.name`)}</span>
        <span className="khelp-tour__meta">
          {status === 'changed' ? t('help.tourChanged') : t('help.tourLength')}
        </span>
      </span>
      <button type="button" className="khelp-btn khelp-btn--primary" onClick={() => help.actions.startTour(tour.id)}>
        {status === 'done' ? t('help.tourAgain') : t('help.takeTour')}
      </button>
    </div>
  );
}

function Skeleton() {
  const { t } = useI18n();
  return (
    <div className="khelp-skeleton" aria-busy="true" aria-live="polite">
      <span className="khelp-visually-hidden">{t('help.loading')}</span>
      <span className="khelp-skeleton__line" /><span className="khelp-skeleton__line khelp-skeleton__line--short" />
      <span className="khelp-skeleton__line" />
    </div>
  );
}

function LoadFailed() {
  const { t } = useI18n();
  const help = useHelp()!;
  return (
    <div className="khelp-note" role="alert">
      <p>{t('help.loadFailed')}</p>
      <button type="button" className="khelp-btn" onClick={() => help.actions.reload()}>{t('help.retry')}</button>
    </div>
  );
}

function HomeView({ query, setQuery, searchRef }: { query: string; setQuery: (q: string) => void; searchRef: React.RefObject<HTMLInputElement | null> }) {
  const { t } = useI18n();
  const help = useHelp()!;
  const { content, loading, failed, helpState } = help.state;
  const { pathname, tours, user, roles } = help.meta;
  const searchId = useId();
  const [showAll, setShowAll] = useState(false);

  const topics = useMemo(() => content?.topics || [], [content]);
  const pageTopics = useMemo(() => topicsForPage(topics, pathname), [topics, pathname]);
  const pageTours = useMemo(() => toursForPage(tours, pageTopics, pathname), [tours, pageTopics, pathname]);
  const otherTopics = useMemo(() => {
    const onPage = new Set(pageTopics.map((x) => x.id));
    return topics.filter((x) => !onPage.has(x.id)).sort((a, b) => a.order - b.order || a.title.localeCompare(b.title));
  }, [topics, pageTopics]);

  const textCache = useRef(new Map<string, string>());
  const textOf = useCallback((e: HelpEntry) => {
    const key = `${e.id}|${e.updated_at || ''}|${e.body.length}`;
    let text = textCache.current.get(key);
    if (text === undefined) { text = helpPlainText(e.body); textCache.current.set(key, text); }
    return text;
  }, []);
  const deferredQuery = React.useDeferredValue(query);
  const hits = useMemo(
    () => (deferredQuery.trim() ? searchHelp([...topics, ...(content?.news || [])], deferredQuery, textOf) : []),
    [deferredQuery, topics, content, textOf],
  );

  const unread = content ? unreadNews(content.news, helpState.whats_new_seen_at).length : 0;
  const role = primaryHelpRole(user);
  const settings = usePublicSettings();
  const mailto = feedbackMailto({
    subject: t('help.feedback.subject'),
    intro: t('help.feedback.intro'),
    page: `${window.location.origin}${pathname}`,
    role,
    to: supportEmail(settings),
  });
  const visibleOthers = showAll ? otherTopics : otherTopics.slice(0, 5);
  const open = (entry: HelpEntry) => help.actions.setView(entry.kind === 'news' ? { name: 'news' } : { name: 'topic', id: entry.id });

  return (
    <>
      <div className="khelp-search" role="search">
        <label htmlFor={searchId} className="khelp-visually-hidden">{t('help.search.label')}</label>
        <Search size={16} aria-hidden="true" className="khelp-search__icon" />
        <input
          ref={searchRef}
          id={searchId}
          type="search"
          className="khelp-search__input"
          placeholder={t('help.search.placeholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape' && query) { e.preventDefault(); e.nativeEvent.stopImmediatePropagation(); setQuery(''); } }}
          autoComplete="off"
          spellCheck={false}
          enterKeyHint="search"
        />
      </div>

      {query.trim() ? (
        <section className="khelp-section" aria-labelledby={`${searchId}-results`}>
          <h3 id={`${searchId}-results`} className="khelp-section__title" aria-live="polite">
            {hits.length ? t('help.search.results', { count: hits.length }) : t('help.search.none')}
          </h3>
          {hits.length > 0 ? (
            <ul className="khelp-list">
              {hits.map((hit) => <TopicRow key={hit.entry.id} topic={hit.entry} snippet={hit.snippet} onOpen={() => open(hit.entry)} />)}
            </ul>
          ) : (
            <p className="khelp-muted">{t('help.search.noneHint')}</p>
          )}
        </section>
      ) : (
        <>
          <section className="khelp-section" aria-labelledby={`${searchId}-page`}>
            <h3 id={`${searchId}-page`} className="khelp-section__title">{t('help.onThisPage')}</h3>
            {pageTours.map((tour) => <TourCard key={tour.id} tour={tour} />)}
            {failed && !content && <LoadFailed />}
            {!content && !failed && loading && <Skeleton />}
            {content && (pageTopics.length > 0 ? (
              <ul className="khelp-list">
                {pageTopics.map((topic) => <TopicRow key={topic.id} topic={topic} onOpen={() => open(topic)} />)}
              </ul>
            ) : (
              <p className="khelp-muted">{t('help.nothingOnPage')}</p>
            ))}
          </section>

          {content && otherTopics.length > 0 && (
            <section className="khelp-section" aria-labelledby={`${searchId}-all`}>
              <h3 id={`${searchId}-all`} className="khelp-section__title">
                {roles.includes('hr') ? t('help.allTopicsForRole') : t('help.allTopics')}
              </h3>
              <ul className="khelp-list">
                {visibleOthers.map((topic) => <TopicRow key={topic.id} topic={topic} onOpen={() => open(topic)} />)}
              </ul>
              {otherTopics.length > visibleOthers.length && (
                <button type="button" className="khelp-link-btn" onClick={() => setShowAll(true)}>
                  {t('help.showAll', { count: otherTopics.length })}
                </button>
              )}
            </section>
          )}
        </>
      )}

      <nav className="khelp-more" aria-label={t('help.moreLabel')}>
        <button type="button" className="khelp-more__item" onClick={() => help.actions.setView({ name: 'news' })}>
          <Sparkles size={16} aria-hidden="true" />
          <span className="khelp-more__label">{t('help.news.title')}</span>
          {unread > 0 && <span className="khelp-count" aria-label={t('help.news.unread', { count: unread })}>{unread}</span>}
          <ChevronRight size={16} aria-hidden="true" className="khelp-row__go" />
        </button>
        <button type="button" className="khelp-more__item" onClick={() => help.actions.setView({ name: 'shortcuts' })}>
          <Keyboard size={16} aria-hidden="true" />
          <span className="khelp-more__label">{t('help.shortcuts.title')}</span>
          <ChevronRight size={16} aria-hidden="true" className="khelp-row__go" />
        </button>
        <a className="khelp-more__item" href={mailto}>
          <Mail size={16} aria-hidden="true" />
          <span className="khelp-more__label">
            {t('help.feedback.title')}
            <span className="khelp-more__hint">{t('help.feedback.hint')}</span>
          </span>
        </a>
        {/* The privacy notice and the terms of use (privacy plan item 2). */}
        <Link className="khelp-more__item" to="/legal/privacy" onClick={closeOnPhone(help.actions.closePanel)}>
          <ShieldCheck size={16} aria-hidden="true" />
          <span className="khelp-more__label">{t('legal.privacy')}</span>
          <ChevronRight size={16} aria-hidden="true" className="khelp-row__go" />
        </Link>
        <Link className="khelp-more__item" to="/legal/terms" onClick={closeOnPhone(help.actions.closePanel)}>
          <FileText size={16} aria-hidden="true" />
          <span className="khelp-more__label">{t('legal.terms')}</span>
          <ChevronRight size={16} aria-hidden="true" className="khelp-row__go" />
        </Link>
        {content?.can_create && (
          <button type="button" className="khelp-more__item" onClick={() => help.actions.setView({ name: 'edit', id: null, kind: 'topic' })}>
            <Plus size={16} aria-hidden="true" />
            <span className="khelp-more__label">{t('help.newTopic')}</span>
          </button>
        )}
      </nav>
    </>
  );
}

function EditedNote({ entry }: { entry: HelpEntry }) {
  const { t, formatDate } = useI18n();
  if (entry.source === 'default' || !entry.updated_at) return null;
  const when = formatDate(entry.updated_at, { day: 'numeric', month: 'short', year: 'numeric' });
  return (
    <p className="khelp-meta">
      {entry.updated_by_name ? t('help.editedBy', { name: entry.updated_by_name, date: when }) : t('help.editedOn', { date: when })}
    </p>
  );
}

function closeOnPhone(close: () => void) {
  return () => { if (isPhone()) close(); };
}

function TopicView({ id }: { id: string }) {
  const { t } = useI18n();
  const help = useHelp()!;
  const { content, failed } = help.state;
  if (!content) return failed ? <LoadFailed /> : <Skeleton />;
  const topic = content.topics.find((x) => x.id === id);
  if (!topic) return <p className="khelp-note">{t('help.topicMissing')}</p>;
  const tour = getTour(topic.tour);
  const canTour = !!tour && help.meta.tours.some((x) => x.id === tour.id);
  return (
    <article className="khelp-article" aria-labelledby={`khelp-topic-${topic.id}`}>
      <div className="khelp-article__head">
        <h3 id={`khelp-topic-${topic.id}`} className="khelp-article__title">{topic.title}</h3>
        {topic.can_edit && (
          <button type="button" className="khelp-btn khelp-btn--quiet" onClick={() => help.actions.setView({ name: 'edit', id: topic.id, kind: 'topic' })}>
            <Pencil size={14} aria-hidden="true" />{t('help.edit')}
          </button>
        )}
      </div>
      <AudienceChip roles={topic.roles} />
      {canTour && tour && <TourCard tour={tour} />}
      <HelpBody markdown={topic.body} onOpenTopic={help.actions.openTopic} onNavigate={closeOnPhone(help.actions.closePanel)} />
      <EditedNote entry={topic} />
    </article>
  );
}

function NewsView() {
  const { t, formatDate } = useI18n();
  const help = useHelp()!;
  const { content, failed, helpState } = help.state;
  const { markNewsSeen } = help.actions;
  // What was unread when the view opened stays marked "New" while it is on screen.
  const [unreadIds] = useState(() => new Set(content ? unreadNews(content.news, helpState.whats_new_seen_at).map((n) => n.id) : []));

  useEffect(() => {
    if (content) markNewsSeen();
  }, [content, markNewsSeen]);

  if (!content) return failed ? <LoadFailed /> : <Skeleton />;
  return (
    <>
      {content.can_create && (
        <div className="khelp-toolbar">
          <button type="button" className="khelp-btn" onClick={() => help.actions.setView({ name: 'edit', id: null, kind: 'news' })}>
            <Plus size={14} aria-hidden="true" />{t('help.news.add')}
          </button>
        </div>
      )}
      {content.news.length === 0 && <p className="khelp-muted">{t('help.news.empty')}</p>}
      <ol className="khelp-news">
        {content.news.map((entry) => (
          <li key={entry.id} className="khelp-news__item">
            <article aria-labelledby={`khelp-news-${entry.id}`}>
              <div className="khelp-news__meta">
                <time dateTime={entry.date || undefined}>{entry.date ? formatDate(`${entry.date}T12:00:00`, { day: 'numeric', month: 'long', year: 'numeric' }) : ''}</time>
                {unreadIds.has(entry.id) && <span className="khelp-chip khelp-chip--new">{t('help.news.new')}</span>}
              </div>
              <div className="khelp-article__head">
                <h3 id={`khelp-news-${entry.id}`} className="khelp-news__title">{entry.title}</h3>
                {entry.can_edit && (
                  <button type="button" className="khelp-icon-btn khelp-icon-btn--small" onClick={() => help.actions.setView({ name: 'edit', id: entry.id, kind: 'news' })} aria-label={t('help.editNamed', { title: entry.title })}>
                    <Pencil size={14} aria-hidden="true" />
                  </button>
                )}
              </div>
              <HelpBody markdown={entry.body} onOpenTopic={help.actions.openTopic} onNavigate={closeOnPhone(help.actions.closePanel)} />
            </article>
          </li>
        ))}
      </ol>
    </>
  );
}

function ShortcutsView() {
  const { t } = useI18n();
  const help = useHelp()!;
  const mac = isMac();
  const staff = help.meta.roles.includes('hr');
  return (
    <>
      {SHORTCUT_GROUPS.filter((g) => !g.staffOnly || staff).map((group) => (
        <section key={group.title} className="khelp-section" aria-label={t(group.title)}>
          <h3 className="khelp-section__title">{t(group.title)}</h3>
          <dl className="khelp-keys">
            {group.items.map((item) => (
              <div key={item.label} className="khelp-keys__row">
                <dt className="khelp-keys__caps">
                  {(mac && item.mac ? item.mac : item.keys).map((k) => <kbd key={k} className="khelp-kbd">{k}</kbd>)}
                </dt>
                <dd className="khelp-keys__label">{t(item.label)}</dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
      <p className="khelp-muted">{t('help.shortcuts.typingNote')}</p>
    </>
  );
}
